import { useState } from 'react';
import { Loader2, LogOut, RefreshCw, ShieldQuestion } from 'lucide-react';
import { StageFlowLogo } from './StageFlowLogo';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { useAuth } from '../data/AuthContext';

// Shown when someone is signed in with Supabase (typically via Google) but
// no team member profile on the roster matches their account. An admin adds
// them to the team with the same email, then "Check again" links the
// profile without a fresh sign-in round-trip.
export function NoAccess() {
  const { sessionEmail, refreshProfile, logout } = useAuth();
  const [checking, setChecking] = useState(false);

  const checkAgain = async () => {
    setChecking(true);
    try {
      const found = await refreshProfile();
      if (!found) {
        toast.info('Still no team member profile linked to this account.');
      }
      // On success AuthContext sets currentUser and AuthGate re-renders
      // straight into the app — nothing else to do here.
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#1e1b4b] p-4">
      <div className="flex items-center gap-3 mb-8">
        <StageFlowLogo className="w-12 h-12" />
        <div>
          <h1 className="text-[24px] tracking-tight text-white">StageFlow</h1>
          <p className="text-[13px] text-indigo-300">Theater Company Management</p>
        </div>
      </div>

      <div className="w-full max-w-md">
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-400/15 mb-4">
            <ShieldQuestion className="w-5 h-5 text-amber-300" />
          </div>
          <h2 className="text-[16px] text-white/90 mb-2">Account not linked to the team</h2>
          <p className="text-[13px] text-indigo-300 leading-relaxed mb-5">
            You're signed in as{' '}
            <span className="text-white/90 font-medium">{sessionEmail || 'your account'}</span>, but no
            StageFlow team member profile matches this account. Ask an admin to add you to the team
            with this exact email address, then check again.
          </p>

          <div className="space-y-2.5">
            <Button
              onClick={checkAgain}
              disabled={checking}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 gap-2"
            >
              {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Check again
            </Button>
            <Button
              onClick={() => logout()}
              variant="outline"
              className="w-full bg-white/5 border-white/15 text-white/90 hover:bg-white/10 hover:text-white gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
